import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Joyride, {
  Step,
  CallBackProps,
  STATUS,
  TooltipRenderProps,
} from "react-joyride";
import { setTourCompleted, setActiveTour, updateTourStep } from "../../redux/tour/action";
import { motion, AnimatePresence } from "framer-motion";
import "../../styles/index.css";
import "../../styles/tour-fix.css"; // Import tour fix CSS

interface GameTourProps {
  steps: Step[];
  isActive?: boolean;
  tourId: string;
  onComplete?: () => void;
  onSkip?: () => void;
  autoStart?: boolean;
  continuous?: boolean;
  showSkipButton?: boolean;
  showProgress?: boolean;
  spotlightClicks?: boolean;
  disableOverlay?: boolean;
  disableScrolling?: boolean;
  styles?: any;
  forceActive?: boolean;
  forcedSteps?: boolean;
  showArrows?: boolean;
  arrowColor?: string;
  arrowSize?: number;
  advanceOnTargetClick?: boolean;
  advanceDelay?: number; // Delay in ms before advancing to next step after click
}

// Custom tooltip component that can be hidden when using arrows
const CustomTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  skipProps,
  isLastStep,
}: TooltipRenderProps) => {
  // If the step has a custom data property to hide tooltip
  if ((step as any).hideTooltip) {
    return null;
  }

  // Get the placement and determine arrow path
  const placement = step.placement || "bottom";
  const arrowSize = 40;
  const arrowColor = "#ff7000";

  // Get arrow path based on placement
  const getArrowPath = (placement: string) => {
    switch (placement) {
      case "top":
        return "M12 20L12 4M12 4L6 10M12 4L18 10"; // Pointing up
      case "bottom":
        return "M12 4L12 20M12 20L18 14M12 20L6 14"; // Pointing down
      case "left":
        return "M20 12L4 12M4 12L10 6M4 12L10 18"; // Pointing left
      case "right":
        return "M4 12L20 12M20 12L14 6M20 12L14 18"; // Pointing right
      case "top-left":
        return "M18 18L4 4M4 4L4 12M4 4L12 4"; // Pointing top-left
      case "top-right":
        return "M4 18L18 4M18 4L10 4M18 4L18 12"; // Pointing top-right
      case "bottom-left":
        return "M18 4L4 18M4 18L12 18M4 18L4 10"; // Pointing bottom-left
      case "bottom-right":
        return "M4 4L18 18M18 18L10 18M18 18L18 10"; // Pointing bottom-right
      default:
        return "M12 4L12 20M12 20L18 14M12 20L6 14"; // Default pointing down
    }
  };

  const arrowPath = getArrowPath(placement);

  return (
    <div {...tooltipProps} className="CustomTooltip" style={{ overflow: 'hidden', maxWidth: '100%' }}>
      {/* Static Arrow - with constrained positioning to prevent overflow */}
      <div
        style={{
          position: "absolute",
          bottom: placement === "top" ? "100%" : "auto",
          top: placement === "bottom" ? "100%" : "auto",
          left: placement === "right" ? "100%" : "auto",
          right: placement === "left" ? "100%" : "auto",
          width: arrowSize,
          height: arrowSize,
          zIndex: 9999,
          pointerEvents: "none",
          filter: `drop-shadow(0 0 5px ${arrowColor})`,
          transform: "translate(-50%, -50%) rotate(180deg)",
          // overflow: "hidden",
        }}
      >
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <svg
            className="animated-arrow"
            width={arrowSize}
            height={arrowSize}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            style={{ overflow: "hidden" }}
          >
            <path
              d={arrowPath}
              stroke={arrowColor}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Add glow effect */}
            <path
              d={arrowPath}
              stroke={arrowColor}
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeOpacity="0.5"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

// Helper function to check if an element is interactive
const isInteractiveElement = (element: HTMLElement): boolean => {
  const interactiveTags = [
    "A",
    "BUTTON",
    "INPUT",
    "SELECT",
    "TEXTAREA",
    "LABEL",
    "OPTION",
  ];
  
  const interactiveRoles = [
    "button",
    "link",
    "checkbox",
    "radio",
    "menuitem",
    "tab",
    "switch",
  ];

  // Check if the element itself is interactive
  if (interactiveTags.includes(element.tagName)) {
    return true;
  }

  // Check for interactive roles
  const role = element.getAttribute("role");
  if (role && interactiveRoles.includes(role)) {
    return true;
  }

  // Check for event listeners (approximate method)
  if (element.onclick || element.getAttribute("onclick")) {
    return true;
  }

  // Check for parent interactive elements
  const closestInteractive =
    element.closest(interactiveTags.join(",")) || element.closest("[role]");

  return !!closestInteractive;
};

// Component to manage tours in different parts of the game
const GameTour: React.FC<GameTourProps> = ({
  steps,
  isActive,
  tourId,
  onComplete,
  onSkip,
  autoStart = false,
  continuous = true,
  showSkipButton = true,
  showProgress = true,
  spotlightClicks = false,
  disableOverlay = false,
  disableScrolling = false,
  styles = {},
  forceActive = false,
  forcedSteps = false,
  showArrows = false,
  arrowColor = "#ff7000",
  arrowSize = 40,
  advanceOnTargetClick = false,
  advanceDelay = 150, // Default delay to allow native events to complete
}) => {
  const dispatch = useDispatch();
  const { completedTours, tourSteps, tourRunStates } = useSelector((state: any) => state.tour);
  
  // Initialize step index from Redux store if available, otherwise use 0
  const [stepIndex, setStepIndex] = useState(() => {
    return tourSteps?.[tourId] || 0;
  });
  
  // Get run state from Redux, fall back to local state
  const reduxRunState = tourRunStates?.[tourId];
  const [localRun, setLocalRun] = useState(false);
  
  // Use Redux run state if available, otherwise use local state
  const run = reduxRunState !== undefined ? reduxRunState : localRun;
  
  const [targetElement, setTargetElement] = useState<Element | null>(null);
  const [arrowPlacement, setArrowPlacement] = useState("bottom");
  
  // Add refs to prevent infinite loops and excessive operations
  const activationCheckCountRef = useRef(0);
  const lastElementCheckTimeRef = useRef(0);
  const elementCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingCallbackRef = useRef(false);
  
  // Memoized tour completion status
  const isTourCompleted = useMemo(() => completedTours?.[tourId], [completedTours, tourId]);

  // Stable callback handlers
  const handleComplete = useCallback(() => {
    setLocalRun(false);
    setStepIndex(0);
    if (onComplete) {
      onComplete();
    }
    dispatch(setTourCompleted(tourId));
    dispatch(setActiveTour(null));
  }, [onComplete, dispatch, tourId]);

  const handleSkip = useCallback(() => {
    // Don't allow skipping if steps are forced
    if (forcedSteps) {
      return;
    }

    setLocalRun(false);
    setStepIndex(0);
    if (onSkip) {
      onSkip();
    }
    dispatch(setTourCompleted(tourId));
    dispatch(setActiveTour(null));
  }, [forcedSteps, onSkip, dispatch, tourId]);

  // Stable next step handler
  const goToNextStep = useCallback(() => {
    const nextIndex = stepIndex + 1;
    if (nextIndex < steps.length) {
      setStepIndex(nextIndex);
      // Store the current step in Redux
      dispatch(updateTourStep(tourId, nextIndex));

      // Check if the next step's target element is available
      const nextStep = steps[nextIndex];
      if (nextStep?.target) {
        // Start polling for the target element if it doesn't exist yet
        waitForElement(nextStep.target as string);
      }
    } else {
      handleComplete();
    }
  }, [stepIndex, steps, dispatch, tourId, handleComplete]);

  // Optimized element waiting function with proper cleanup
  const waitForElement = useCallback((selector: string) => {
    // Rate limit element checking to prevent excessive DOM queries
    const now = Date.now();
    if (now - lastElementCheckTimeRef.current < 500) {
      return;
    }
    lastElementCheckTimeRef.current = now;

    // Check immediately
    const element = document.querySelector(selector);
    if (element) {
      setTargetElement(element);
      return;
    }

    // Clear any existing interval
    if (elementCheckIntervalRef.current) {
      clearInterval(elementCheckIntervalRef.current);
    }

    // If not found, start polling with throttling
    let attempts = 0;
    const maxAttempts = 25; // Reduced from 50 to prevent excessive checking
    elementCheckIntervalRef.current = setInterval(() => {
      attempts++;
      const element = document.querySelector(selector);

      if (element) {
        // Element found
        if (elementCheckIntervalRef.current) {
          clearInterval(elementCheckIntervalRef.current);
          elementCheckIntervalRef.current = null;
        }
        setTargetElement(element);
      } else if (attempts >= maxAttempts) {
        // Give up after maxAttempts
        if (elementCheckIntervalRef.current) {
          clearInterval(elementCheckIntervalRef.current);
          elementCheckIntervalRef.current = null;
        }
        // console.warn(`Tour target element not found: ${selector}`);
      }
    }, 400); // Increased interval to reduce overhead
  }, []);

  // Effect to handle tour activation with proper safeguards
  useEffect(() => {
    // Prevent excessive activation checks
    activationCheckCountRef.current++;
    if (activationCheckCountRef.current > 10) {
      return;
    }
    
    // Always respect forceActive flag regardless of other conditions
    if (forceActive && !run) {
      // console.log(`Force activating GameTour "${tourId}"`);
      setLocalRun(true);
      dispatch({ 
        type: 'SET_TOUR_RUN_STATE', 
        payload: { tourId, running: true } 
      });
      return;
    }
    
    // Handle normal activation conditions - ONLY through explicit isActive
    // Removed autoStart condition to prevent unwanted tour activation
    if (!isTourCompleted && isActive && !run) {
      // console.log(`Setting GameTour "${tourId}" run to true from explicit activation`);
      setLocalRun(true);
      dispatch({ 
        type: 'SET_TOUR_RUN_STATE', 
        payload: { tourId, running: true } 
      });
    }
  }, [isActive, isTourCompleted, run, reduxRunState, forceActive, forcedSteps, dispatch, tourId]);

  // Stable step index update effect
  useEffect(() => {
    if (run) {
      dispatch(updateTourStep(tourId, stepIndex));
    }
  }, [stepIndex, tourId, dispatch, run]);

  // Optimized target element observation effect
  useEffect(() => {
    if (!run) {
      // Clear any existing intervals when tour is not running
      if (elementCheckIntervalRef.current) {
        clearInterval(elementCheckIntervalRef.current);
        elementCheckIntervalRef.current = null;
      }
      return;
    }

    const currentStep = steps[stepIndex];
    if (!currentStep?.target) return;

    // Wait for the current step's target to be available
    const cleanup = waitForElement(currentStep.target as string);

    // Set arrow placement based on step configuration or default to bottom
    const stepPlacement = currentStep.placement || "bottom";
    setArrowPlacement(stepPlacement);

    // Add click listener if advanceOnTargetClick is enabled
    if (advanceOnTargetClick || forcedSteps) {
      // We'll set up the click handler after we find the element
      const clickHandlerInterval = setInterval(() => {
        const element = document.querySelector(currentStep.target as string);
        if (element) {
          clearInterval(clickHandlerInterval);

          const clickHandler = (e: Event) => {
            const target = e.target as HTMLElement;
            if (!target) return;

            // Check if the clicked element is interactive
            if (isInteractiveElement(target)) {
              // For interactive elements, wait for their native events to complete
              setTimeout(goToNextStep, advanceDelay);
            } else {
              // For non-interactive elements, advance immediately
              goToNextStep();
            }
          };

          element.addEventListener("click", clickHandler);

          // Store the handler and element for cleanup
          return () => {
            element.removeEventListener("click", clickHandler);
            clearInterval(clickHandlerInterval);
          };
        }
      }, 400); // Increased interval to reduce overhead

      // Cleanup function for the interval
      return () => {
        if (cleanup) cleanup();
        clearInterval(clickHandlerInterval);
        if (elementCheckIntervalRef.current) {
          clearInterval(elementCheckIntervalRef.current);
          elementCheckIntervalRef.current = null;
        }
      };
    }

    return () => {
      if (cleanup) cleanup();
      if (elementCheckIntervalRef.current) {
        clearInterval(elementCheckIntervalRef.current);
        elementCheckIntervalRef.current = null;
      }
    };
  }, [stepIndex, run, steps, advanceOnTargetClick, forcedSteps, waitForElement, goToNextStep, advanceDelay]);

  // Optimized Joyride callback handler
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    // Prevent concurrent callback processing
    if (isProcessingCallbackRef.current) {
      return;
    }
    
    isProcessingCallbackRef.current = true;
    
    try {
      const { action, index, status, type } = data;

      // Update step index in Redux store for persistence
      if (type === "step:after" && index !== undefined) {
        dispatch(updateTourStep(tourId, index));
      }

      // Handle tour completion or skip
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        if (status === STATUS.FINISHED) {
          dispatch(setTourCompleted(tourId));
          dispatch(setActiveTour(null));
          handleComplete();
        } else if (status === STATUS.SKIPPED) {
          dispatch(setTourCompleted(tourId));
          dispatch(setActiveTour(null));
          handleSkip();
        }
      }
    } finally {
      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingCallbackRef.current = false;
      }, 100);
    }
  }, [dispatch, tourId, handleComplete, handleSkip]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clear all intervals and timeouts on unmount
      if (elementCheckIntervalRef.current) {
        clearInterval(elementCheckIntervalRef.current);
      }
    };
  }, []);

  // Render tour components
  return (  <>
      <Joyride
        callback={handleJoyrideCallback}
        continuous={continuous}
        hideCloseButton={true}
        run={run}
        scrollToFirstStep={true}  
        showProgress={showProgress}
        showSkipButton={showSkipButton && !forcedSteps}
        steps={steps}
        stepIndex={stepIndex}
        styles={{
          ...styles,
          options: {
            ...styles?.options,
            zIndex: 9999,
            overlayColor: 'rgba(0, 0, 0, 0.65)',
            width: 'auto',
            maxWidth: '90vw', // Limit maximum width to prevent overflow
          }
        }}
        tooltipComponent={CustomTooltip}
        disableOverlay={forcedSteps ? false : disableOverlay}
        disableOverlayClose={forcedSteps}
        disableScrolling={disableScrolling}
        spotlightClicks={forcedSteps ? true : spotlightClicks}
        floaterProps={{
          disableAnimation: true,
          hideArrow: true,
          styles: {
            floater: {
              maxWidth: '90vw',
              overflow: 'hidden'
            },
            container: {
              overflow: 'hidden'
            }
          }
        }}
        spotlightPadding={10}
        scrollOffset={100}
        scrollDuration={300}
      />
    </>
  );
};

export default GameTour;
